
import { Box, Stack, Typography } from "@mui/material";

import { typeScale } from "../theme/tokens";
import { WidgetRenderer } from "../widgets/registry";

import { PageTitle } from "./PageTitle";
import type { PageTemplateProps } from "./types";

// Shared layout for non-Welcome page templates: clean type-led title block
// (the navy chrome lives in the room nav now) with optional intro, then the
// page's widgets in a vertical stack.
//
// If a template needs a custom layout (Welcome's contact-card column,
// future Compliance Checklist with side actions, etc.), it skips BasePage
// and renders its own.
// No supplier-name eyebrow above the H1 on any page (removed 2026-09-03):
// the nav and the logo already carry the supplier's name.
//
// `fullWidthIntro` lets the intro run the full content width instead of the
// 760px reading measure; it then renders under the title row rather than
// beside the logo, so it is exactly as wide as the widgets below it. The
// Agreements, Onboarding Plan and Partnership Form pages use it (2026-09-03).
//
// `children` render after the widget stack, for a template whose body is a
// component rather than widgets (the Content Optimization matrix and
// checkout, 2026-09-23); they share the H1 row and the logo.
//
// `maxWidth` replaces the 1280px cap: "none" runs the page to the window's
// right edge (Your assortment, Checkout); a number sets the column, as Our
// capabilities does to match the proposal's (Eric, 2026-09-24).
//
// `compactHeader` tightens the space above and below the title (Partnership Form).
//
// `fillViewport` makes the page a viewport-height column (md and up) and
// hands the remaining height to the widget stack, so a widget that can
// stretch (the Google Doc frame) ends at the bottom of the window and the
// page itself never scrolls. The Partnership Form page uses it (2026-09-03).
export function BasePage({
    page,
    room,
    fullWidthIntro,
    fillViewport,
    maxWidth = 1280,
    compactHeader,
    children,
}: PageTemplateProps & { fullWidthIntro?: boolean; fillViewport?: boolean; compactHeader?: boolean; maxWidth?: number | "none"; children?: React.ReactNode }) {
    const intro = page.data?.intro as string | undefined;

    const { logoUrl, supplierName } = room.supplier;

    return (
        // Left-anchored to the drawer (no auto-centering gutter), capped wide
        // for readability — matches the admin-dashboard content flow.
        <Box
            sx={{
                px: { xs: 3, md: 4 },
                pt: compactHeader ? { xs: 2, md: 3 } : { xs: 4, md: 6 },
                // The viewport-filling page keeps only 8px under the doc frame's
                // link (Bill, 2026-09-05); other pages keep the full bottom gutter.
                pb: fillViewport ? 1 : { xs: 4, md: 6 },
                maxWidth,
                ...(fillViewport && {
                    height: { md: "100vh" },
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }),
            }}
        >
            <Box
                sx={{
                    mb: compactHeader ? 2 : 5,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 3,
                    flexWrap: "wrap",
                }}
            >
                {/* On a phone the title takes the full line and the logo sits
                    above it, so the 36px page title is not squeezed beside it. */}
                <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0 }}>
                    <Typography
                        variant="h1"
                        sx={{
                            ...typeScale.pageTitle,
                            mt: 0,
                            mb: intro && !fullWidthIntro ? 2 : 0,
                        }}
                    >
                        <PageTitle title={page.title} highlight={page.data?.titleHighlight as string | undefined} />
                    </Typography>
                    {intro && !fullWidthIntro && (
                        <Typography
                            variant="body1"
                            sx={{ color: "text.secondary", ...typeScale.lead, maxWidth: 1064 }}
                        >
                            {intro}
                        </Typography>
                    )}
                </Box>
                {/* Supplier logo — the room's brand cue now that the chrome is the
                    Bison-standard drawer. Sits to the right of the page title. */}
                {logoUrl && (
                    <Box
                        component="img"
                        src={logoUrl}
                        alt={supplierName}
                        sx={{
                            height: { xs: 40, md: 56 },
                            maxWidth: { xs: 140, md: 200 },
                            objectFit: "contain",
                            flexShrink: 0,
                            mt: 0.5,
                            order: { xs: -1, sm: 0 },
                        }}
                    />
                )}
            </Box>
            {intro && fullWidthIntro && (
                <Typography
                    variant="body1"
                    // The viewport-filling page (Partnership Form) sits its widget
                    // 16px under the intro instead of 40px (Bill, 2026-09-04).
                    sx={{ color: "text.secondary", ...typeScale.lead, mt: -3, mb: fillViewport ? 2 : 5 }}
                >
                    {intro}
                </Typography>
            )}

            <Stack spacing={4} sx={fillViewport ? { flex: 1, minHeight: 0 } : undefined}>
                {page.widgets.map((widget) => (
                    <WidgetRenderer key={widget.id} widget={widget} room={room} />
                ))}
                {children}
            </Stack>
        </Box>
    );
}
